/**
 * burst-guard / read-budget: in-flight dedupe plus a short TTL. Every caller that asks for the
 * same key while an answer is loading or younger than `ttlMs` shares it; failures are never
 * cached. `ttlMs <= 0` disables sharing (each call loads).
 */
export function shortCache<T>(ttlMs: number, now: () => number = Date.now) {
  const entries = new Map<string, { p: Promise<T>; at?: number }>()
  const get = (key: string, load: () => Promise<T>): Promise<T> => {
    if (ttlMs <= 0) return load()
    const t = now()
    for (const [k, e] of entries) if (e.at !== undefined && t - e.at >= ttlMs) entries.delete(k)
    const hit = entries.get(key)
    if (hit) return hit.p
    const entry: { p: Promise<T>; at?: number } = { p: load() }
    entries.set(key, entry)
    entry.p.then(
      () => {
        entry.at = now()
      },
      () => {
        if (entries.get(key) === entry) entries.delete(key)
      },
    )
    return entry.p
  }
  /** Drop settled answers (in-flight ones stay shared: they started after the change). */
  const clearSettled = () => {
    for (const [k, e] of entries) if (e.at !== undefined) entries.delete(k)
  }
  /** Drop everything, in flight or not. */
  const clear = () => entries.clear()
  return Object.assign(get, { clearSettled, clear })
}
